import {
  NOTIFICATION_CATEGORIES,
  type NotificationCategory,
} from '../notification-category/notification-category';
import {
  PREFERENCE_CHANNELS,
  type NotificationChannel,
  type PreferenceChannel,
} from '../notification-channel/notification-channel';
import {
  templatePlaceholders,
  type NotificationTemplateKey,
} from '../notification-template-key/notification-template-key';

/**
 * What a template's placeholder may be filled with.
 *
 * `datetime` exists so the renderer can **format** rather than paste: the
 * `NotificationRequested` payload carries scalars, so a suspension end arrives as
 * an ISO string, and a member reading "حتى 2026-09-07T14:02:11.000Z" in a push is
 * exactly the lowest-common-denominator output U0 forbids. Declaring the kind is
 * what lets the delivery pipeline render it in the recipient's locale and zone.
 *
 * There is deliberately **no `boolean`**. A `{{reversed}}` interpolating "true"
 * is never the sentence anybody wanted; a fact worth telling a member differently
 * is worth a different template key, which is why appeal verdicts are two keys
 * rather than one with a flag in it.
 */
export type NotificationParamKind = 'text' | 'number' | 'datetime';

export interface NotificationParamSpec {
  name: string;
  kind: NotificationParamKind;
  /**
   * A required parameter that arrives missing or `null` **fails the render
   * closed** — the delivery is recorded with the reason and nothing is sent.
   *
   * An optional one renders as the empty string, and the seeded copy is written
   * so that reads correctly. That distinction is not decoration: an appeal
   * verdict can genuinely have no violation type behind it (the row's type is
   * nullable, because an AI refusal has no type to name), and refusing to tell a
   * member their appeal succeeded because of it would be absurd.
   */
  required: boolean;
}

/**
 * One notification the platform can send, declared in code (N17).
 *
 * ## Why a code catalogue and not just the rows
 *
 * `notification_templates` holds the **copy**; this holds the **contract** — who
 * it is about, which preference switch governs it, what may be interpolated into
 * it and where tapping it goes. Three surfaces read it and none of them could be
 * built from the rows alone: the admin editor (which parameters may this copy
 * name?), the defaults grid (which categories exist?), and P20b's
 * `legalNoticeTemplateKey` field, which is free text today precisely because
 * there was nothing to offer as a choice.
 *
 * It is also what makes a template a **checked** artefact: saving copy whose
 * placeholder the platform never supplies is refused at the edit, rather than
 * discovered as a broken notice by the member it was about.
 */
export interface NotificationCatalogueEntry {
  key: NotificationTemplateKey;
  category: NotificationCategory;
  /** The channels this notice may travel on. */
  channels: readonly NotificationChannel[];
  params: readonly NotificationParamSpec[];
  /**
   * Transactional and compliance notices ignore the member's preferences
   * (`03-notifications.md`): a verification decision, an enforcement notice and
   * a legal re-consent are things the platform is obliged to say, and the mobile
   * preferences screen shows them as "always on" rather than as a switch that
   * does nothing.
   */
  bypassesPreferences: boolean;
  /**
   * A **challenge, not a notice** (P30a): a one-time code the member is
   * waiting for. It never enters the pipeline — whose delivery rows persist the
   * rendered words for staff, and a code readable by anyone holding
   * `notifications.read` is no secret — but goes synchronously through
   * `DIRECT_MESSAGE_PORT`, which writes a delivery row with the words **null**.
   * The dispatcher refuses it, the violation-notice picker and the campaign
   * composer never list it (`isDeliverableTemplateKey`), and it is never a
   * preference. What stays staff-editable is the copy: the template row.
   */
  direct: boolean;
  /**
   * Where tapping the notice goes — a path template in the same `{{param}}`
   * grammar as the copy, or `null` when the notice opens nothing in particular.
   *
   * A path rather than a route object so exactly one mechanism renders it, and
   * so the stored value on the notification row stays meaningful after the
   * client's navigation is rearranged. `notification-catalogue.spec.ts` asserts
   * every placeholder here is a declared parameter.
   */
  deepLink: string | null;
}

/**
 * A key **family**: a prefix whose members are authored per row, with a platform
 * template standing behind them.
 *
 * There is exactly one, and it exists because P20a's violation catalogue seeds
 * six types each naming `moderation.violation.<key>` — keys no template row has.
 * The alternatives were to teach `modules/notifications` moderation's own
 * catalogue (a coupling neither module wants) or to leave six enforcement
 * notices undeliverable. Instead an unauthored per-type key resolves to
 * {@link MODERATION_VIOLATION_NOTICE_KEY}, and authoring the per-type template
 * overrides it — which is what P21b's picker is for.
 */
export interface NotificationTemplateFamily {
  prefix: string;
  fallbackKey: NotificationTemplateKey;
}

export const MODERATION_VIOLATION_NOTICE_KEY = 'moderation.notice.violation';

/**
 * The guardian gate's three keys (P24d2).
 *
 * Named constants rather than literals at the publish sites, the discipline
 * `moderation/domain/enforcement-notices.tokens.ts` keeps: a key spelled by hand
 * in a service is a key that can be misspelled into a notice nobody ever
 * receives, and nothing would report it — the render simply finds no template.
 */
export const GUARDIAN_APPROVAL_REQUESTED_TEMPLATE_KEY = 'guardian.approval.requested';
export const GUARDIAN_APPROVAL_PENDING_TEMPLATE_KEY = 'guardian.approval.pending';
export const GUARDIAN_CONVERSATION_OPENED_TEMPLATE_KEY = 'guardian.conversation.opened';

/**
 * The booking's five keys (P25f), for the same reason the guardian block above
 * is named rather than spelled.
 *
 * **`venueName` is a parameter here and `venueId` deliberately is not.** Venue
 * names are N5-translated, so the name cannot be chosen by the publisher — one
 * string would go to both members whatever they read (P22a). The job carries the
 * **id** out of band, as `campaignId` travels, and the dispatcher folds the
 * per-locale name in after it has resolved the recipient. A uuid is not something
 * copy may ever print at a member, which is why it is not declared.
 */
export const MEETING_BOOKED_TEMPLATE_KEY = 'meetings.booking.requested';
export const MEETING_CONFIRMED_TEMPLATE_KEY = 'meetings.booking.confirmed';
export const MEETING_GUARDIAN_REQUESTED_TEMPLATE_KEY = 'meetings.booking.guardian-requested';
export const MEETING_CANCELLED_TEMPLATE_KEY = 'meetings.booking.cancelled';
export const MEETING_REMINDER_TEMPLATE_KEY = 'meetings.booking.reminder';
export const MEETING_EXPIRED_TEMPLATE_KEY = 'meetings.booking.expired';

/**
 * The re-review's three keys (D6), named for the reason the guardian block is.
 *
 * `opened` is **in-app only** and carries no deep link: the member is gated to
 * the verification stage the moment the case opens, so a push would tell them
 * what the screen in front of them already says. The two decisions travel on
 * both channels — the member is back in the app when they arrive — and land on
 * the answers screen, where an accepted edit reads as new and a refused one is
 * shown restored with the reviewer's reason.
 */
export const REREVIEW_OPENED_TEMPLATE_KEY = 'verification.rereview.opened';
export const REREVIEW_ACCEPTED_TEMPLATE_KEY = 'verification.rereview.accepted';
export const REREVIEW_REFUSED_TEMPLATE_KEY = 'verification.rereview.refused';

/**
 * The paid, slot-booked interview's four keys (D9a).
 *
 * All four carry **no deep link**: a member waiting on verification is gated to
 * that stage, so the app opens on the screen that already shows the appointment
 * (the same argument as `REREVIEW_OPENED_TEMPLATE_KEY`). `startsAt` is a
 * `datetime` so the pipeline renders it in the recipient's own locale and zone
 * rather than pasting an ISO string into a push; the method and the place are
 * deliberately not parameters — one is a translated word and the other a link
 * that must not travel in a notification — and the screen carries both.
 */
export const INTERVIEW_BOOKED_TEMPLATE_KEY = 'verification.interview.booked';
export const INTERVIEW_MOVED_TEMPLATE_KEY = 'verification.interview.moved';
/** The place or link of a booked appointment was set or changed; the time was not. */
export const INTERVIEW_LOCATION_SHARED_TEMPLATE_KEY = 'verification.interview.location-shared';
export const INTERVIEW_REMINDER_TEMPLATE_KEY = 'verification.interview.reminder';
export const INTERVIEW_FEE_SETTLED_TEMPLATE_KEY = 'verification.interview-fee.settled';

/**
 * The evidence was accepted and the interview opened (F4) — two keys rather
 * than one with a branch, because a template has no conditionals and the
 * member is either asked to pay or asked to pick a time. Parameterless and
 * without a deep link for the same reason as the four above.
 */
export const INTERVIEW_UNLOCKED_TEMPLATE_KEY = 'verification.interview.unlocked';
export const INTERVIEW_UNLOCKED_FEE_DUE_TEMPLATE_KEY = 'verification.interview.unlocked-fee-due';

const text = (name: string, required = true): NotificationParamSpec => ({
  name,
  kind: 'text',
  required,
});

const datetime = (name: string, required = true): NotificationParamSpec => ({
  name,
  kind: 'datetime',
  required,
});

/** The first `number` parameter arrived with P22a's discount value. */
const number = (name: string, required = true): NotificationParamSpec => ({
  name,
  kind: 'number',
  required,
});

/**
 * Push + in-app, the pair every notice carried before the email channel and the
 * pair real-time social traffic keeps.
 *
 * Named for what it is rather than "both" — there are three channels now — and
 * deliberately not derived from the enum, so adding a channel adds it to no
 * notice by accident.
 */
const PUSH_AND_IN_APP: readonly NotificationChannel[] = ['push', 'inApp'];

/**
 * The three channels a durable notice travels on (P21e2). Listed for the same
 * reason `PUSH_AND_IN_APP` is: a fourth channel joins a notice by decision.
 */
const WITH_EMAIL: readonly NotificationChannel[] = ['push', 'inApp', 'email'];

/**
 * The keys that also travel by **email** (P21e).
 *
 * The rule that decides membership: email carries what a member wants in an
 * inbox after the phone is gone — decisions, consequences, receipts,
 * appointments, announcements — never real-time social traffic. A listed
 * constant, asserted equal to the catalogue's own email set, so the next key to
 * gain email is a decision written here and not a drift.
 *
 * Stays push + in-app: likes, matches, messages, the guardian gate, the
 * re-review, the course discounts, and a booking *request* (the ask, not the
 * appointment) — every one of them is answered on the phone, and a mail about
 * it would arrive after the moment had passed.
 */
export const EMAIL_TEMPLATE_KEYS: readonly NotificationTemplateKey[] = [
  MODERATION_VIOLATION_NOTICE_KEY,
  'moderation.notice.legal-consequences',
  'moderation.notice.no-refund-on-ban',
  'moderation.notice.appeal-upheld',
  'moderation.notice.appeal-overturned',
  'verification.decision.approved',
  'verification.decision.rejected',
  INTERVIEW_BOOKED_TEMPLATE_KEY,
  INTERVIEW_MOVED_TEMPLATE_KEY,
  INTERVIEW_LOCATION_SHARED_TEMPLATE_KEY,
  INTERVIEW_REMINDER_TEMPLATE_KEY,
  INTERVIEW_FEE_SETTLED_TEMPLATE_KEY,
  INTERVIEW_UNLOCKED_TEMPLATE_KEY,
  INTERVIEW_UNLOCKED_FEE_DUE_TEMPLATE_KEY,
  'announcements.campaign',
  'legal.document.updated',
  'billing.direct-payment.activated',
  'billing.direct-payment.rejected',
  MEETING_CONFIRMED_TEMPLATE_KEY,
  MEETING_CANCELLED_TEMPLATE_KEY,
  MEETING_REMINDER_TEMPLATE_KEY,
  MEETING_EXPIRED_TEMPLATE_KEY,
];

/**
 * The one-time code that proves an email address (P30a). Named for the reason
 * the guardian block is; issued by `VerificationChallengeService` and by nothing
 * else.
 */
export const EMAIL_VERIFICATION_CODE_TEMPLATE_KEY = 'auth.email-verification.code';

/**
 * The one-time code that proves a phone number (P30b1). Rendered only by a
 * `send` SMS provider — a `managed` one (Twilio Verify) composes its own words
 * and never reads this row, which the template editor says in as many words.
 */
export const PHONE_VERIFICATION_CODE_TEMPLATE_KEY = 'auth.phone-verification.code';

/**
 * The keys that are **challenges** rather than notices — see `direct` on the
 * entry. A listed constant asserted equal to the catalogue's own direct set, so
 * the next code is a decision written here.
 *
 * Deliberately **not** in {@link EMAIL_TEMPLATE_KEYS}: that list names what the
 * pipeline carries by email, and a direct key never enters the pipeline.
 */
export const DIRECT_TEMPLATE_KEYS: readonly NotificationTemplateKey[] = [
  EMAIL_VERIFICATION_CODE_TEMPLATE_KEY,
  PHONE_VERIFICATION_CODE_TEMPLATE_KEY,
];

/**
 * The keys that travel by **sms** (P30b1) — every one of them direct, because
 * `sms` is a channel a code rides on and never a preference a member may
 * switch; the catalogue spec asserts exactly that. Listed for the reason
 * {@link EMAIL_TEMPLATE_KEYS} is: the next key to gain sms is a decision.
 */
export const SMS_TEMPLATE_KEYS: readonly NotificationTemplateKey[] = [
  PHONE_VERIFICATION_CODE_TEMPLATE_KEY,
];

/**
 * Every template key the platform emits today.
 *
 * Added by the phase that introduces the notice — the discipline `queue.tokens.ts`
 * keeps for queues. Keys for events that do not exist yet are **not** listed:
 * `courses.discount.granted` joined the list with P22a, which is the phase that
 * emits `DiscountGranted`, `billing.direct-payment.activated` with P23a3, which
 * emits `DirectPaymentActivated`, and the five `meetings.booking.*` keys with
 * P25f, which is the phase that subscribes to the booking events P25c publishes.
 */
const CATALOGUE: readonly NotificationCatalogueEntry[] = [
  // ---- moderation (N20) — every one of these bypasses preferences, because a
  // consequence a member can switch off is a consequence they first learn about
  // when something stops working.
  {
    key: MODERATION_VIOLATION_NOTICE_KEY,
    category: 'moderation',
    channels: WITH_EMAIL,
    params: [text('violationTypeKey'), text('action'), datetime('suspensionUntil', false)],
    bypassesPreferences: true,
    direct: false,
    deepLink: '/violations',
  },
  {
    key: 'moderation.notice.legal-consequences',
    category: 'moderation',
    channels: WITH_EMAIL,
    params: [],
    bypassesPreferences: true,
    direct: false,
    deepLink: null,
  },
  {
    key: 'moderation.notice.no-refund-on-ban',
    category: 'moderation',
    channels: WITH_EMAIL,
    params: [],
    bypassesPreferences: true,
    direct: false,
    deepLink: null,
  },
  {
    key: 'moderation.notice.appeal-upheld',
    category: 'moderation',
    channels: WITH_EMAIL,
    params: [text('violationTypeKey', false), text('action', false)],
    bypassesPreferences: true,
    direct: false,
    deepLink: '/violations',
  },
  {
    key: 'moderation.notice.appeal-overturned',
    category: 'moderation',
    channels: WITH_EMAIL,
    params: [text('violationTypeKey', false), text('action', false)],
    bypassesPreferences: true,
    direct: false,
    deepLink: '/violations',
  },

  // ---- verification: a decision changes which stack the member's app renders,
  // so it is transactional in the strictest sense.
  {
    key: 'verification.decision.approved',
    category: 'verification',
    channels: WITH_EMAIL,
    params: [],
    bypassesPreferences: true,
    direct: false,
    deepLink: null,
  },
  {
    key: 'verification.decision.rejected',
    category: 'verification',
    channels: WITH_EMAIL,
    params: [text('reasonCode', false)],
    bypassesPreferences: true,
    direct: false,
    deepLink: null,
  },
  {
    key: REREVIEW_OPENED_TEMPLATE_KEY,
    category: 'verification',
    channels: ['inApp'],
    params: [number('count')],
    bypassesPreferences: true,
    direct: false,
    deepLink: null,
  },
  {
    key: REREVIEW_ACCEPTED_TEMPLATE_KEY,
    category: 'verification',
    channels: PUSH_AND_IN_APP,
    params: [],
    bypassesPreferences: true,
    direct: false,
    deepLink: '/profile/answers',
  },
  {
    key: REREVIEW_REFUSED_TEMPLATE_KEY,
    category: 'verification',
    channels: PUSH_AND_IN_APP,
    params: [text('reasonCode', false)],
    bypassesPreferences: true,
    direct: false,
    deepLink: '/profile/answers',
  },
  {
    key: INTERVIEW_BOOKED_TEMPLATE_KEY,
    category: 'verification',
    channels: WITH_EMAIL,
    params: [datetime('startsAt')],
    bypassesPreferences: true,
    direct: false,
    deepLink: null,
  },
  {
    key: INTERVIEW_MOVED_TEMPLATE_KEY,
    category: 'verification',
    channels: WITH_EMAIL,
    params: [datetime('startsAt')],
    bypassesPreferences: true,
    direct: false,
    deepLink: null,
  },
  {
    key: INTERVIEW_LOCATION_SHARED_TEMPLATE_KEY,
    category: 'verification',
    channels: WITH_EMAIL,
    params: [datetime('startsAt')],
    bypassesPreferences: true,
    direct: false,
    deepLink: null,
  },
  {
    key: INTERVIEW_REMINDER_TEMPLATE_KEY,
    category: 'verification',
    channels: WITH_EMAIL,
    params: [datetime('startsAt')],
    bypassesPreferences: true,
    direct: false,
    deepLink: null,
  },
  {
    key: INTERVIEW_FEE_SETTLED_TEMPLATE_KEY,
    category: 'verification',
    channels: WITH_EMAIL,
    params: [],
    bypassesPreferences: true,
    direct: false,
    deepLink: null,
  },
  {
    key: INTERVIEW_UNLOCKED_TEMPLATE_KEY,
    category: 'verification',
    channels: WITH_EMAIL,
    params: [],
    bypassesPreferences: true,
    direct: false,
    deepLink: null,
  },
  {
    key: INTERVIEW_UNLOCKED_FEE_DUE_TEMPLATE_KEY,
    category: 'verification',
    channels: WITH_EMAIL,
    params: [],
    bypassesPreferences: true,
    direct: false,
    deepLink: null,
  },

  // ---- matching
  {
    key: 'matching.match.created',
    category: 'matches',
    channels: PUSH_AND_IN_APP,
    params: [text('matchId')],
    bypassesPreferences: false,
    direct: false,
    deepLink: '/matches/{{matchId}}',
  },
  {
    // Deliberately parameterless: naming the member who liked you in a push
    // notification would disclose, on a lock screen, a fact the platform only
    // discloses after a mutual like (N13's structural rule, one layer out).
    key: 'matching.like.received',
    category: 'likes',
    channels: PUSH_AND_IN_APP,
    params: [],
    bypassesPreferences: false,
    direct: false,
    deepLink: '/likes',
  },

  // ---- chat: the conversation id and nothing else. The message body never
  // travels in a notification payload.
  {
    key: 'chat.message.received',
    category: 'messages',
    channels: PUSH_AND_IN_APP,
    params: [text('conversationId')],
    bypassesPreferences: false,
    direct: false,
    deepLink: '/conversations/{{conversationId}}',
  },

  // ---- guardian (P24d2): the three notices the match-approval gate needs.
  //
  // The gate landed in P24b2 and told nobody for a whole phase, because the wali
  // had no client for a notice to arrive at. P24d1 built it, and these close the
  // loop: he is asked, she is told she is waiting, and both members are told when
  // it opens.
  //
  // None of them names a member, which is the same rule the like notice keeps one
  // comment block down. The wali's is disambiguated by its destination instead,
  // and his ward's *label* is his own private note about her — `GUARDIAN_LIST_FOR_WARD`
  // withholds it from every other module by design.
  {
    // **The one deep link on the platform that is not a conversation or a list of
    // the member's own things.** It cannot be the conversation: the mobile
    // resolver refuses any path over two segments, and a wali reaches a thread
    // only through his ward. That limit turned out to be the better design —
    // one ward can have several matches waiting, and her list is where each of
    // them is actionable.
    key: GUARDIAN_APPROVAL_REQUESTED_TEMPLATE_KEY,
    category: 'guardian',
    channels: PUSH_AND_IN_APP,
    params: [text('wardUserId')],
    bypassesPreferences: false,
    direct: false,
    deepLink: '/wards/{{wardUserId}}',
  },
  {
    // Her conversation, not `null`. P24b2 already built the whole waiting state
    // on that screen — "Waiting for your guardian… you can read what was said,
    // but not reply yet" — so the notice lands somewhere that explains itself.
    // Telling her something is happening and giving her nowhere to look is the
    // version of this notice that generates a support message.
    key: GUARDIAN_APPROVAL_PENDING_TEMPLATE_KEY,
    category: 'guardian',
    channels: PUSH_AND_IN_APP,
    params: [text('conversationId')],
    bypassesPreferences: false,
    direct: false,
    deepLink: '/conversations/{{conversationId}}',
  },
  {
    // Sent to **both** members, and its copy deliberately never says a guardian
    // approved anything. Two reasons: the counterparty has no guardian
    // relationship in view and would be told about a third party they know
    // nothing of, and the same notice covers the release path — where the
    // conversation opens precisely because nobody is left to approve it.
    key: GUARDIAN_CONVERSATION_OPENED_TEMPLATE_KEY,
    category: 'guardian',
    channels: PUSH_AND_IN_APP,
    params: [text('conversationId')],
    bypassesPreferences: false,
    direct: false,
    deepLink: '/conversations/{{conversationId}}',
  },

  // ---- announcements (P21c): the one key a staff-composed campaign sends
  // under. The seeded template is a **pass-through wrapper** — the campaign's own
  // translated copy arrives as `title` and `body` — which is what lets a campaign
  // carry its own words (N5 lists `campaigns` (content) as a translation table)
  // while every notification on the platform still renders from a template row
  // (N17). A brand that wants every announcement prefixed edits the wrapper once.
  //
  // It does **not** bypass preferences, and that is the whole reason the key is
  // not operator-writable: an operator who could name the key could compose an
  // announcement that reached members who switched announcements off.
  {
    key: 'announcements.campaign',
    category: 'announcements',
    channels: WITH_EMAIL,
    params: [text('title'), text('body')],
    bypassesPreferences: false,
    direct: false,
    deepLink: null,
  },

  // ---- legal (N16): seeded here so P21c's campaign dispatch has copy to send.
  {
    key: 'legal.document.updated',
    category: 'legal',
    channels: WITH_EMAIL,
    params: [text('documentKey'), datetime('effectiveAt')],
    bypassesPreferences: true,
    direct: false,
    deepLink: '/legal/{{documentKey}}',
  },

  // ---- courses (P22a) — a discount was earned.
  //
  // ## Two keys, not one with a type in it
  //
  // "20% off" and "20 off" are different sentences in both languages, and the
  // fixed one needs a currency word the percentage one must not have. This file
  // already refuses `boolean` params for exactly this reason, and appeal
  // verdicts are two keys rather than one with a flag — the same discipline.
  //
  // ## The course is NOT named in the copy
  //
  // A `text` param is one string for every recipient, and a course title is
  // translated (N5). Interpolating it would send Arabic readers an English
  // title, or the reverse, depending on which locale the *issuing* request
  // happened to be in. The discount surface names the source properly localized;
  // the notice says what was earned.
  {
    key: 'courses.discount.granted.percent',
    category: 'courses',
    channels: PUSH_AND_IN_APP,
    params: [number('value'), datetime('expiresAt', false)],
    /**
     * A member may switch this off. It is good news about something they chose
     * to do, not a consequence or an obligation — the two tests
     * `bypassesPreferences` exists for.
     */
    bypassesPreferences: false,
    direct: false,
    /**
     * The member's own discount list (P22c2).
     *
     * One segment and no parameter: the notice says what was earned, and the
     * screen it opens is the whole history — which is the surface that can name
     * the source properly localized and say when it expires, neither of which a
     * `text` param could carry (see the note above).
     *
     * It was `null` from P22a until P22c2, because
     * `resolveNotificationDeepLink`'s spec asserts that *every* link declared
     * here resolves to a real destination — the seam that stops a notice opening
     * nothing — and the screen did not exist. Set here in the same commit as the
     * screen key and the resolver case, which is what that seam is for.
     */
    deepLink: '/discounts',
  },
  {
    key: 'courses.discount.granted.fixed',
    category: 'courses',
    channels: PUSH_AND_IN_APP,
    params: [number('value'), datetime('expiresAt', false)],
    bypassesPreferences: false,
    direct: false,
    /** The same list: the two keys differ in their sentence, not in where they go. */
    deepLink: '/discounts',
  },
  // ---- billing (P23a3) — a staff-recorded payment was confirmed.
  //
  // ## One key, unlike the discount pair
  //
  // There is one sentence to say ("we received your payment, your subscription
  // runs until X") and no branch inside it. The discount notice needed two keys
  // because a percentage and an amount are different sentences; an amount paid
  // is always an amount.
  //
  // ## The PLAN is not named in the copy
  //
  // The same reason the course is not named in `courses.discount.granted.*`: a
  // notification parameter is one string for every recipient, and a product's
  // name is translated (N5). Interpolating it would send an Arabic reader
  // whichever locale the *confirming staff member* happened to be using. The
  // amount and the date are locale-independent; the template renders them in the
  // member's own language.
  {
    key: 'billing.direct-payment.activated',
    category: 'billing',
    channels: WITH_EMAIL,
    params: [number('amount'), text('currency'), datetime('currentPeriodEnd')],
    /**
     * A member may switch this off. It is a receipt for something they chose to
     * do — not a consequence and not an obligation, which are the two tests
     * `bypassesPreferences` exists for, and the spec asserts that only the three
     * compliance categories bypass.
     */
    bypassesPreferences: false,
    direct: false,
    /**
     * The member's own subscription screen (P23c1) — not the paywall.
     *
     * This notice says *"we received your payment, your subscription runs until
     * X"*, so the destination is the standing it is reporting on, where the plan,
     * the period end and the channel are named. Sending a member who has just
     * paid to a list of plans would be the app failing to notice what happened.
     *
     * No id: the subject is the caller's own account, exactly as `/discounts` is.
     */
    deepLink: '/subscription',
  },
  /**
   * A direct payment was refused (F3) — the receipt did not match, the amount
   * was short, the transfer never arrived. One key for both product kinds: the
   * sentence is "your payment was not accepted, here is why", and what the
   * member was paying for is not named for the reason `activated` gives.
   *
   * `reason` is the staff member's own words, forwarded verbatim: it is the
   * one part of this notice that cannot be a template, and a refusal without a
   * why is a support ticket. Not a bypass — the member chose to pay, and the
   * spec pins bypass to the three compliance categories.
   *
   * The deep link is the ORDER, not the standing screen: the member lands on
   * the payment that was refused, reads the reason beside the receipt they
   * sent, and can send a corrected one from the same screen.
   */
  {
    key: 'billing.direct-payment.rejected',
    category: 'billing',
    channels: WITH_EMAIL,
    params: [text('orderId'), text('reason')],
    bypassesPreferences: false,
    direct: false,
    deepLink: '/payments/{{orderId}}',
  },

  // ---- meetings (P25f) — every booking transition, and the reminder.
  //
  // ## `venueName` is supplied by the DISPATCHER, not by the publisher
  //
  // Every one of these wants to name the building, and a venue's name is
  // translated (N5). A notification parameter is one string for every recipient,
  // so a publisher choosing the name would send one member the other's language
  // — P22a's trap, and it bites here harder than anywhere because a booking
  // always has exactly two readers who may not share a locale. The event carries
  // `venueId`, the job carries it out of band as `campaignId` travels, and
  // `NotificationDispatchService.withVenueName` folds the name in **after** the
  // recipient's locale is known. `venueId` is deliberately not a declared
  // parameter: a uuid is never a sentence.
  //
  // ## Why these are domain events rather than `NOTIFICATION_REQUESTED`
  //
  // The generic port mints `notice-<user>-<template>-<occurredAt>` inside its own
  // listener, and one booking transition fans out to two or three people under a
  // single clock read — so two of those notices collide on one BullMQ id and one
  // is silently dropped (P24d2, which lost a ward's second conversation exactly
  // this way). `domain-notices.listener.ts` keys these on the **booking id**.
  {
    key: MEETING_BOOKED_TEMPLATE_KEY,
    category: 'meetings',
    channels: PUSH_AND_IN_APP,
    params: [text('bookingId'), text('venueName'), datetime('startsAt')],
    bypassesPreferences: false,
    direct: false,
    deepLink: '/meetings/{{bookingId}}',
  },
  {
    key: MEETING_CONFIRMED_TEMPLATE_KEY,
    category: 'meetings',
    channels: WITH_EMAIL,
    params: [text('bookingId'), text('venueName'), datetime('startsAt')],
    bypassesPreferences: false,
    direct: false,
    deepLink: '/meetings/{{bookingId}}',
  },
  {
    /**
     * To every wali who is being waited on, and to nobody else.
     *
     * **It names no member — not even the ward.** `GUARDIAN_LIST_FOR_WARD`
     * withholds her label from every other module by design, and the booking's
     * counterparty is somebody a wali may not be shown at all (PROGRESS §Known
     * issues, unowned). `guardian.approval.requested` solved the identical
     * problem with "somebody you look after", and this copy follows it.
     *
     * The deep link is his **ward's booking list**, not the booking: the member
     * booking screen refuses a viewer who is not one of the two members. It
     * cannot be `/wards/{{wardUserId}}` either — that is already her
     * conversations — and the resolver refuses a third segment, so the head
     * differs instead.
     */
    key: MEETING_GUARDIAN_REQUESTED_TEMPLATE_KEY,
    category: 'meetings',
    channels: PUSH_AND_IN_APP,
    params: [text('wardUserId'), text('venueName'), datetime('startsAt')],
    bypassesPreferences: false,
    direct: false,
    deepLink: '/ward-meetings/{{wardUserId}}',
  },
  {
    /**
     * One key for all three ways a meeting is called off — the other member, a
     * staff override, or the match ending — and the copy names **none of them**.
     *
     * P24d2's rule, arriving where it matters most. A second key could only
     * distinguish itself by saying *why*, and every "why" here is a fact about
     * somebody else's account: that he withdrew, that staff intervened, that she
     * unmatched. The recipient is never the canceller (the fan-out skips them),
     * so "your meeting has been cancelled" is true of every path. `reason` lives
     * on the event, where the record wants it and no member reads it.
     *
     * A member must never discover a cancellation by turning up, which is why
     * this key exists at all — `03-meetings.md`'s "all transitions notified" is
     * ambiguous about it and the ambiguity was resolved deliberately, not skipped.
     */
    key: MEETING_CANCELLED_TEMPLATE_KEY,
    category: 'meetings',
    channels: WITH_EMAIL,
    params: [text('bookingId'), text('venueName'), datetime('startsAt')],
    bypassesPreferences: false,
    direct: false,
    deepLink: '/meetings/{{bookingId}}',
  },
  {
    /**
     * `meetings.reminderHoursBefore` before the slot starts, to the two members
     * and to nobody else.
     *
     * **Not to the guardians**, deliberately: a wali confirmed that a meeting
     * *may* happen, he is not attending it, and a man standing over several
     * wards would otherwise collect a stream of reminders for rooms he will
     * never enter. A reminder is for the people who have to be somewhere.
     */
    key: MEETING_REMINDER_TEMPLATE_KEY,
    category: 'meetings',
    channels: WITH_EMAIL,
    params: [text('bookingId'), text('venueName'), datetime('startsAt')],
    bypassesPreferences: false,
    direct: false,
    deepLink: '/meetings/{{bookingId}}',
  },
  {
    /**
     * A request the window ran out on before everyone had answered — to the
     * members who HAD said yes, so the one still waiting stops waiting.
     *
     * The copy names nobody: whether it was the other member or a wali who never
     * replied is a fact about somebody else's account (`MEETING_CANCELLED`'s
     * rule), and the sentence a member needs is that this time is gone and
     * another can be asked for.
     */
    key: MEETING_EXPIRED_TEMPLATE_KEY,
    category: 'meetings',
    channels: WITH_EMAIL,
    params: [text('bookingId'), text('venueName'), datetime('startsAt')],
    bypassesPreferences: false,
    direct: false,
    deepLink: '/meetings/{{bookingId}}',
  },
  // ---- account (P30a) — challenges. `direct`: never dispatched, never a
  // preference, never a campaign; the words are rendered for one send and
  // stored nowhere. `expiresInMinutes` is a `number` so the copy can say "in
  // {{expiresInMinutes}} minutes" in either locale without the api choosing
  // the sentence.
  {
    key: EMAIL_VERIFICATION_CODE_TEMPLATE_KEY,
    category: 'account',
    channels: ['email'],
    params: [text('code'), number('expiresInMinutes')],
    bypassesPreferences: true,
    direct: true,
    deepLink: null,
  },
  {
    key: PHONE_VERIFICATION_CODE_TEMPLATE_KEY,
    category: 'account',
    channels: ['sms'],
    params: [text('code'), number('expiresInMinutes')],
    bypassesPreferences: true,
    direct: true,
    deepLink: null,
  },
];

export const NOTIFICATION_CATALOGUE = CATALOGUE;

export const NOTIFICATION_TEMPLATE_FAMILIES: readonly NotificationTemplateFamily[] = [
  { prefix: 'moderation.violation.', fallbackKey: MODERATION_VIOLATION_NOTICE_KEY },
];

const BY_KEY: ReadonlyMap<string, NotificationCatalogueEntry> = new Map(
  CATALOGUE.map((entry) => [entry.key, entry]),
);

/** The entry declared under exactly this key, or `null`. */
export function getCatalogueEntry(key: string): NotificationCatalogueEntry | null {
  return BY_KEY.get(key) ?? null;
}

/**
 * The entry that governs a key: the exact declaration, else the family it
 * belongs to.
 *
 * The **contract** a per-type key inherits — its category, its parameters, its
 * bypass — is the family's, whether or not somebody has authored bespoke copy
 * for that type. That is what stops `moderation.violation.self_harm` and
 * `moderation.violation.spam_scam` needing eleven catalogue entries between
 * them, and what makes a violation type an operator can create at runtime
 * deliverable at all.
 */
export function resolveCatalogueEntry(key: string): NotificationCatalogueEntry | null {
  const exact = BY_KEY.get(key);
  if (exact !== undefined) {
    return exact;
  }

  for (const family of NOTIFICATION_TEMPLATE_FAMILIES) {
    if (key.startsWith(family.prefix) && key.length > family.prefix.length) {
      return BY_KEY.get(family.fallbackKey) ?? null;
    }
  }

  return null;
}

/**
 * The template key whose **copy** should be rendered for a requested key: the
 * key itself when a row for it exists, otherwise its family's fallback.
 *
 * Separate from {@link resolveCatalogueEntry} because the two answer different
 * questions — this one needs to know what the database holds, and only the
 * caller does.
 */
export function fallbackTemplateKeyFor(key: string): string | null {
  if (BY_KEY.has(key)) {
    return null;
  }

  for (const family of NOTIFICATION_TEMPLATE_FAMILIES) {
    if (key.startsWith(family.prefix) && key.length > family.prefix.length) {
      return family.fallbackKey;
    }
  }

  return null;
}

/**
 * Whether a key is one the platform knows how to deliver **as a notice**: a
 * declared entry that is not `direct`. What the violation-notice picker, the
 * campaign composer and the dispatcher ask; a code answers false here and is
 * reached only through `DIRECT_MESSAGE_PORT`.
 */
export function isDeliverableTemplateKey(key: string): boolean {
  const entry = resolveCatalogueEntry(key);
  return entry !== null && !entry.direct;
}

/**
 * Every placeholder a template under this key is allowed to name.
 *
 * The admin save path checks copy against this, so "your account was restricted
 * until {{until}}" — where the platform supplies `suspensionUntil` — is refused
 * by the form rather than delivered with the braces still in it.
 */
export function allowedPlaceholdersFor(key: string): readonly string[] {
  return (resolveCatalogueEntry(key)?.params ?? []).map((param) => param.name);
}

/**
 * The placeholders in a piece of copy that the catalogue does not supply.
 *
 * Empty means the copy is renderable. Used by the template service and by the
 * catalogue's own spec, which asserts every seeded deep link is renderable too.
 */
export function unknownPlaceholders(key: string, ...texts: readonly string[]): readonly string[] {
  const allowed = allowedPlaceholdersFor(key);
  const seen: string[] = [];

  for (const value of texts) {
    for (const placeholder of templatePlaceholders(value)) {
      if (!allowed.includes(placeholder) && !seen.includes(placeholder)) {
        seen.push(placeholder);
      }
    }
  }

  return seen;
}

/**
 * The channels at least one notice in a category travels on.
 *
 * What the two defaults grids read to draw a cell as a switch or as inert: a
 * switch for a channel nothing in the category sends on would govern nothing,
 * and P21b drew exactly that for `system` before this existed.
 */
export function channelsCarriedBy(
  category: NotificationCategory,
): ReadonlySet<NotificationChannel> {
  const carried = new Set<NotificationChannel>();

  for (const entry of CATALOGUE) {
    // A direct entry's channel is how a challenge reaches the member, not a
    // preference anybody may switch — so it draws no cell in either grid.
    if (entry.category === category && !entry.direct) {
      for (const channel of entry.channels) {
        carried.add(channel);
      }
    }
  }

  return carried;
}

/** One category's default for every preference channel. */
export type ChannelPreferences = Record<PreferenceChannel, boolean>;

/**
 * The coded default for `notifications.defaultPreferences`, **derived** from the
 * category list rather than typed out beside it.
 *
 * The brief for this phase is explicit about why: the setting used to hold nine
 * free-form record keys, so the admin defaults grid and the pipeline could
 * disagree about what a category was called and nothing would say so. Generating
 * it means adding a category cannot leave the setting behind.
 *
 * Everything starts **on**. A member who has consented to an account has
 * consented to being told things about it; the switches exist for the member who
 * later decides otherwise, not as an obstacle at the start.
 */
export function defaultPreferencesFromCatalogue(): Record<
  NotificationCategory,
  ChannelPreferences
> {
  const defaults = {} as Record<NotificationCategory, ChannelPreferences>;

  for (const category of NOTIFICATION_CATEGORIES) {
    const preferences = {} as ChannelPreferences;
    for (const channel of PREFERENCE_CHANNELS) {
      preferences[channel] = true;
    }
    defaults[category] = preferences;
  }

  return defaults;
}
